import type { PushDevice, PushSubscriptionInput } from "@beacon/core/chat-push"
import { queryOptions } from "@tanstack/react-query"
import { call } from "@/administration/client.ts"
import type { InstallRoute } from "@/installed-app/install-route.ts"
import { keyBytes, madeWith, pushStateOf, type PushState } from "./push-state.ts"

export type { PushState } from "./push-state.ts"

/*
 * Chat push on this browser: whether it can push at all, whether the Member let it, and turning
 * it on or off. Nothing is asked of the browser until the Member presses to turn it on, which asks
 * its permission from that press (as iOS requires) and registers it for them (`turnOnPush`); a
 * browser that can't push, or whose Member said no, simply never notifies, and Chat works the same.
 * The server keeps each browser for the session that registered it (`chat-push`), so every visit
 * keeps it current (`readPushState`), only for the Member who turned it on: whoever else signs in
 * here finds it off until they turn it on themselves. Signing out forgets it on both sides
 * (`forgetPushHere`); a session found lapsed forgets it here (`dropPushHere`).
 */

/** Whether this browser has what pushing takes: a service worker, the Push API, notifications. */
export function pushSupported() {
  try {
    return (
      "serviceWorker" in navigator && typeof PushManager !== "undefined" && "Notification" in window
    )
  } catch {
    return false
  }
}

/** The service worker's registration, none where there is none (`sst dev` has none). */
async function registration() {
  if (!pushSupported()) return undefined
  return (await navigator.serviceWorker.getRegistration()) ?? undefined
}

/** This browser's push subscription, none when it has none or can't say. */
async function subscriptionHere() {
  try {
    return (await (await registration())?.pushManager.getSubscription()) ?? undefined
  } catch {
    return undefined
  }
}

const asInput = (subscription: PushSubscription) =>
  subscription.toJSON() as unknown as PushSubscriptionInput

/** The stage's public key, none while its push is off. Asked once a session. */
export const pushKeyQuery = queryOptions({
  queryKey: ["chat", "push", "key"],
  queryFn: () => call<{ publicKey: string | null }>("GET", "/chat/push"),
  staleTime: Infinity,
})

/** Where push stands on this browser (`PushState`), read from the browser and the server. */
export const pushDeviceKey = ["chat", "push", "device"] as const

/**
 * Reads where push stands here, keeping this browser current while it is subscribed (as every
 * visit does), so the answer says its previews: off when the server holds it for nobody signed in
 * here.
 */
export async function readPushState(
  installRoute: InstallRoute,
  publicKey: string | null,
): Promise<PushState> {
  const supported = pushSupported()
  const permission = supported ? Notification.permission : "default"
  const subscription = supported && publicKey ? await subscriptionHere() : undefined
  const registered =
    subscription && permission === "granted" && publicKey && madeWith(subscription, publicKey)
      ? await kept("PATCH", subscription).catch(() => null)
      : null
  return pushStateOf({ supported, installRoute, publicKey, permission, registered })
}

/**
 * Registers this browser for the Member (`PUT`, from their press alone) or keeps the one they
 * registered current (`PATCH`, refused when it isn't theirs).
 */
const kept = (method: "PUT" | "PATCH", subscription: PushSubscription, previews?: boolean) =>
  call<PushDevice>(method, "/chat/push/subscription", {
    subscription: asInput(subscription),
    ...(previews !== undefined && { previews }),
  })

/**
 * Turns push on here, from the Member's press: asks the browser's permission, subscribes with the
 * stage's key, and registers the subscription. Answers the permission when it wasn't given.
 */
export async function turnOnPush(publicKey: string): Promise<NotificationPermission> {
  const permission = await Notification.requestPermission()
  if (permission !== "granted") return permission
  const worker = await registration()
  if (!worker) throw new Error("no service worker")
  const existing = await worker.pushManager.getSubscription()
  if (existing && !madeWith(existing, publicKey)) await existing.unsubscribe()
  const subscription =
    existing && madeWith(existing, publicKey)
      ? existing
      : await worker.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: keyBytes(publicKey),
        })
  await kept("PUT", subscription)
  return permission
}

/** Turns this browser's previews on or off. */
export async function setPushPreviews(previews: boolean) {
  const subscription = await subscriptionHere()
  if (!subscription) throw new Error("not subscribed")
  return kept("PATCH", subscription, previews)
}

/** Turns push off here: the server forgets this browser, and the browser its subscription. */
export async function turnOffPush() {
  const subscription = await subscriptionHere()
  if (!subscription) return
  await call<void>("DELETE", "/chat/push/subscription", { endpoint: subscription.endpoint })
  await subscription.unsubscribe()
}

/**
 * At signing out, before the session ends: the server forgets this browser and the browser its
 * subscription, so whoever signs in here next is never notified of the Member's Chat. Best effort,
 * and quick: the session ending stops the pushes anyway.
 */
export async function forgetPushHere() {
  const subscription = await subscriptionHere()
  if (!subscription) return
  await Promise.race([
    call<void>("DELETE", "/chat/push/subscription", { endpoint: subscription.endpoint }).catch(
      () => undefined,
    ),
    new Promise((resolve) => setTimeout(resolve, 2000)),
  ])
  await subscription.unsubscribe().catch(() => false)
}

/**
 * Once the app finds its session lapsed (or its Member deactivated): the browser forgets its
 * subscription, so nobody who signs in here next inherits it. The server can't be asked without a
 * session; it forgets the browser itself when it next finds that session ended.
 */
export async function dropPushHere() {
  const subscription = await subscriptionHere()
  await subscription?.unsubscribe().catch(() => false)
}
